/**
 * SmartInspect — Client Inputs (inline-editable consultant working area).
 * Reuses the existing ROI questionnaire field components (SmartInspectField,
 * SmartInspectPackageSelect). Every value is inline editable with autosave via
 * the shared assessment update API — no separate edit mode, no popup, no edit
 * button. Fields are rendered dynamically from the configured active +
 * customer-visible SmartInspect questions (respecting display order).
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { ChevronDown, ChevronRight, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { siListQuestions, siListPackages, siUpdateAssessment, siImplementationInvestment, siGetImplementationRules, groupSteps, countryCascadePatch, currencyCascadePatch, findCountryQuestion } from "../lib/smartInspectApi";
import { SmartInspectField } from "./SmartInspectField";
import { SmartInspectPackageSelect } from "./SmartInspectPackageSelect";

const SECTION_TITLE = {
  "Current Inspection Time": "Current Inspection Process",
  "Storage and IT": "Storage & IT",
  "Claims and Reporting": "Claims & Reporting",
};

const slug = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
const num = (v) => Number(v || 0);

function CollapsibleSection({ title, count, defaultOpen = true, testid, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[#F8FAFC] transition-colors"
        data-testid={`${testid}-toggle`}
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown className="w-4 h-4 text-[#9CA3AF]" /> : <ChevronRight className="w-4 h-4 text-[#9CA3AF]" />}
          <span className="text-sm font-semibold text-[#111827]">{title}</span>
          {count != null && <span className="text-[10px] text-[#9CA3AF]">({count})</span>}
        </div>
      </button>
      {open && <div className="px-4 pb-4 border-t border-[#E5E7EB]">{children}</div>}
    </div>
  );
}

export default function SmartInspectClientInputs({ assessment, onSaved, readOnly = false }) {
  const id = assessment?.id;
  const [questions, setQuestions] = useState(null);
  const [packages, setPackages] = useState([]);
  const [implRules, setImplRules] = useState(null);
  const [values, setValues] = useState(assessment?.answers || {});
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved | error
  const [err, setErr] = useState("");

  const timerRef = useRef(null);
  const pendingRef = useRef({});

  useEffect(() => {
    Promise.all([siListQuestions(), siListPackages().catch(() => []), siGetImplementationRules().catch(() => [])])
      .then(([qs, pks, rules]) => { setQuestions(qs); setPackages(pks || []); setImplRules(rules || []); })
      .catch(() => setErr("Failed to load question catalog"));
  }, []);

  // Re-seed local values when the assessment changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setValues(assessment?.answers || {}); }, [assessment?.id]);

  const flush = useCallback(async () => {
    const patch = pendingRef.current;
    pendingRef.current = {};
    if (!Object.keys(patch).length) return;
    setSaveState("saving");
    try {
      const updated = await siUpdateAssessment(id, { answers: patch });
      setSaveState("saved");
      onSaved?.(updated);
      setTimeout(() => setSaveState((s) => (s === "saved" ? "idle" : s)), 1500);
    } catch (e) {
      setSaveState("error");
      toast.error(e?.response?.data?.detail?.message || "Autosave failed. Please retry.");
    }
  }, [id, onSaved]);

  const scheduleSave = useCallback((patch) => {
    pendingRef.current = { ...pendingRef.current, ...patch };
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flush, 700);
  }, [flush]);

  const setVal = useCallback((key, v) => {
    if (key === "country") {
      const patch = countryCascadePatch(findCountryQuestion(questions), v);
      setValues((prev) => ({ ...prev, ...patch }));
      if (!readOnly) scheduleSave(patch);
      return;
    }
    if (key === "preferred_currency") {
      const patch = currencyCascadePatch(findCountryQuestion(questions), v);
      setValues((prev) => ({ ...prev, ...patch }));
      if (!readOnly) scheduleSave(patch);
      return;
    }
    setValues((prev) => ({ ...prev, [key]: v }));
    if (!readOnly) scheduleSave({ [key]: v });
  }, [readOnly, scheduleSave, questions]);

  if (!assessment) return null;
  if (err) return <div className="text-xs text-red-600" data-testid="si-client-inputs-error">{err}</div>;
  if (!questions) return <div className="text-xs text-[#9CA3AF]" data-testid="si-client-inputs-loading">Loading client inputs…</div>;

  const currency = values.preferred_currency || "USD";
  const annualVolume = num(values.operating_locations) * num(values.inspections_per_day_per_location) * num(values.operating_days_per_year);

  const steps = groupSteps(questions, { customerOnly: true });
  const inputSections = [];
  for (const st of steps) {
    if (st.key === "step4") continue;
    for (const sec of st.sections) {
      if (!sec.questions || sec.questions.length === 0) continue;
      inputSections.push(sec);
    }
  }

  return (
    <div className="space-y-3" data-testid="si-client-inputs">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-[#6B7280]">
          Edit any value below — changes save automatically. Open the ROI Results tab to recalculate the business case.
        </p>
        <div className="text-xs" data-testid="si-autosave-status">
          {saveState === "saving" && <span className="inline-flex items-center gap-1 text-[#6B7280]"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…</span>}
          {saveState === "saved" && <span className="inline-flex items-center gap-1 text-emerald-600"><Check className="w-3.5 h-3.5" /> Saved</span>}
          {saveState === "error" && <span className="text-red-600">Save failed</span>}
        </div>
      </div>

      <div className="space-y-2.5" data-testid="si-client-inputs-summary">
        {inputSections.map((sec) => {
          const title = SECTION_TITLE[sec.section] || sec.section;
          const sslug = slug(sec.section);
          return (
            <CollapsibleSection key={sec.section} title={title} count={sec.questions.length} testid={`si-inputs-section-${sslug}`}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                {sec.questions.map((q) => (
                  <div key={q.field_key} className={q.input_type === "textarea" ? "md:col-span-2" : ""}>
                    <SmartInspectField
                      q={q}
                      value={values[q.field_key]}
                      currency={currency}
                      disabled={readOnly}
                      onChange={(v) => setVal(q.field_key, v)}
                    />
                  </div>
                ))}
              </div>
            </CollapsibleSection>
          );
        })}

        {/* SmartInspect Selection — package + deployment selectors */}
        <CollapsibleSection title="SmartInspect Selection" testid="si-inputs-section-smartinspect-selection">
          <div className="mt-3">
            {readOnly ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><div className="text-[10px] uppercase text-[#9CA3AF]">Selected Package</div><div className="font-medium text-[#111827]">{values.selected_package_name || "—"}</div></div>
                  <div><div className="text-[10px] uppercase text-[#9CA3AF]">Estimated Implementation Investment</div><div className="font-medium text-[#111827]">{currency} {siImplementationInvestment(num(values.operating_locations), implRules, values.pricing_region).toLocaleString()}</div></div>
                </div>
                <div data-testid="si-inputs-addons-readonly">
                  <div className="text-[10px] uppercase text-[#9CA3AF] mb-1">Optional Add-ons</div>
                  {(values.selected_addons_snapshot || []).length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {(values.selected_addons_snapshot || []).map((a) => (
                        <span key={a.name} className="inline-flex items-center gap-1 text-xs bg-[#F0F9FF] text-[#025979] border border-[#025979]/20 rounded-full px-2.5 py-1 font-medium" data-testid={`si-inputs-addon-${(a.name||"").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                          {a.name}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div className="text-xs text-[#9CA3AF]">No add-ons selected.</div>
                  )}
                </div>
              </div>
            ) : (
              <SmartInspectPackageSelect
                packages={packages}
                values={values}
                setVal={setVal}
                annualVolume={annualVolume}
              />
            )}
          </div>
        </CollapsibleSection>
      </div>
    </div>
  );
}
